/* assets/gproperty-owner-fit-flow.js — GPROPERTY-OWNER-FIT-08
 * GP-08 canonical Owner Fit runtime.
 * Consumes verified GProperty draft + public readiness through GPROPERTY_DATA_GATE.
 * Owner conditions stay local until readiness is available and the user explicitly consents to preview creation.
 * No direct localStorage/fetch owner; no technical request/result identifiers in consumer UI.
 */
(function (window, document) {
  'use strict';

  var ROOT = '[data-gproperty-owner-fit]';
  var VERSION = 'gproperty-owner-fit-08';
  var FORBIDDEN_CONSUMER_TOKENS =
    /(?:serving_ready|public_redacted|request[_ ]?id|result[_ ]?id|data_source_not_ready|verified_family_data_unavailable|mock_data|\bDPO\b|\bALS\b|endpoint|contract|fail[- ]?closed|lead[_ ]?id)/i;

  function all(root, selector) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function value(form, name) {
    var field = form.elements.namedItem(name);
    return field
      ? String(field.value || '')
          .replace(/\s+/g, ' ')
          .trim()
      : '';
  }

  function setText(root, selector, next) {
    var node = root.querySelector(selector);
    if (node) node.textContent = next;
  }

  function locale(root) {
    return String(root.getAttribute('data-owner-locale') || 'en').trim();
  }

  function copy(root) {
    var lang = locale(root);
    var en = {
      noAddress: 'No verified address is saved on this device.',
      noAddressDetail: 'Return to GProperty and verify an address before continuing.',
      routeMismatch:
        'This link does not match the verified address saved on this device. Choose the address again before continuing.',
      coordinateVerified: 'Verified location',
      coordinateMissing: 'Location details incomplete',
      intentRequired: 'Choose what you are preparing for first.',
      consentRequired: 'Confirm the preview data use before creating the preview.',
      localSaveFailed: 'This browser could not save the owner conditions.',
      localSaved: 'Owner conditions saved on this device.',
      checkingTitle: 'Checking available public information',
      checkingCopy: 'Your owner conditions can still be saved while availability is checked.',
      checkingBadge: 'Checking',
      readyTitle: 'Preview data is available',
      readyCopy: 'You can save your conditions or create a preview when you choose.',
      readyBadge: 'Available',
      blockedTitle: 'Preview data is not ready yet',
      blockedCopy: 'Your owner conditions can still be saved on this device.',
      blockedBadge: 'Save locally',
      readinessErrorTitle: 'Availability could not be checked',
      readinessErrorCopy: 'Your owner conditions can still be saved on this device.',
      previewLoading: 'Creating preview…',
      submitDraft: 'Save owner conditions',
      submitPreview: 'Create Owner Fit preview',
      failClosed:
        'Your owner conditions are saved on this device. A preview will only be created when the required public information is available.',
      previewError:
        'The preview could not be created. Your owner conditions are still saved on this device.',
      resultTitle: 'Owner Fit preview ready',
      resultCopy: 'Available public information has been organised for this address.',
      resultStatus: 'Ready',
    };

    if (lang === 'zh-Hans') {
      return {
        noAddress: '此设备尚未保存已核实地址。',
        noAddressDetail: '请先返回 GProperty 核实地址后再继续。',
        routeMismatch: '此链接与本设备保存的已核实地址不一致，请重新选择地址后继续。',
        coordinateVerified: '位置已核实',
        coordinateMissing: '位置资料不完整',
        intentRequired: '请先选择你目前准备做什么。',
        consentRequired: '建立预览前，请先确认本次资料使用。',
        localSaveFailed: '此浏览器无法保存业主条件。',
        localSaved: '业主条件已保存在本设备。',
        checkingTitle: '正在检查可用公开资料',
        checkingCopy: '检查期间，业主条件仍可保存在本设备。',
        checkingBadge: '检查中',
        readyTitle: '建立预览所需资料已可用',
        readyCopy: '請先保存条件，也可以在确认后建立预览。',
        readyBadge: '可用',
        blockedTitle: '建立预览所需资料暂未准备好',
        blockedCopy: '你仍可把业主条件保存在本设备。',
        blockedBadge: '仅保存',
        readinessErrorTitle: '暂时无法检查资料状态',
        readinessErrorCopy: '你仍可把业主条件保存在本设备。',
        previewLoading: '正在建立预览…',
        submitDraft: '保存业主条件',
        submitPreview: '建立业主定位预览',
        failClosed: '业主条件已保存在本设备；只有所需公开资料可用时才会建立预览。',
        previewError: '暂时无法建立预览，业主条件仍保存在本设备。',
        resultTitle: '业主定位预览已准备好',
        resultCopy: '已为这个地址整理可用公开资料。',
        resultStatus: '已准备好',
      };
    }

    if (lang === 'zh-Hant') {
      return {
        noAddress: '此裝置尚未儲存已核實地址。',
        noAddressDetail: '請先返回 GProperty 核實地址後再繼續。',
        routeMismatch: '此連結與本裝置儲存的已核實地址不一致，請重新選擇地址後繼續。',
        coordinateVerified: '位置已核實',
        coordinateMissing: '位置資料不完整',
        intentRequired: '請先選擇你目前準備做甚麼。',
        consentRequired: '建立預覽前，請先確認本次資料使用。',
        localSaveFailed: '此瀏覽器無法儲存業主條件。',
        localSaved: '業主條件已儲存在此裝置。',
        checkingTitle: '正在檢查可用公開資料',
        checkingCopy: '檢查期間，業主條件仍可儲存在此裝置。',
        checkingBadge: '檢查中',
        readyTitle: '建立預覽所需資料已可用',
        readyCopy: '請先儲存條件，也可以在確認後建立預覽。',
        readyBadge: '可用',
        blockedTitle: '建立預覽所需資料暫未準備好',
        blockedCopy: '你仍可把業主條件儲存在此裝置。',
        blockedBadge: '只儲存',
        readinessErrorTitle: '暫時無法檢查資料狀態',
        readinessErrorCopy: '你仍可把業主條件儲存在此裝置。',
        previewLoading: '正在建立預覽…',
        submitDraft: '儲存業主條件',
        submitPreview: '建立業主定位預覽',
        failClosed: '業主條件已儲存在此裝置；只有所需公開資料可用時才會建立預覽。',
        previewError: '暫時無法建立預覽，業主條件仍儲存在此裝置。',
        resultTitle: '業主定位預覽已準備好',
        resultCopy: '已為這個地址整理可用公開資料。',
        resultStatus: '已準備好',
      };
    }

    return en;
  }

  function safeConsumerText(gate, input, fallback) {
    var cleaned = gate.clean(input);
    if (!cleaned || FORBIDDEN_CONSUMER_TOKENS.test(cleaned)) return fallback;
    return cleaned.slice(0, 600);
  }

  function setState(root, name, message) {
    root.setAttribute('data-state', name);
    all(root, '[data-owner-state]').forEach(function (node) {
      node.hidden = node.getAttribute('data-owner-state') !== name;
    });
    if (name === 'error' && message) setText(root, '[data-owner-error-copy]', message);
    if (name === 'blocked' && message) setText(root, '[data-owner-blocked-copy]', message);
    var submit = root.querySelector('[data-owner-submit]');
    if (submit) submit.disabled = name === 'loading';
  }

  function readinessUI(root, mode, title, text, badge) {
    var node = root.querySelector('[data-owner-readiness]');
    if (node) node.setAttribute('data-mode', mode);
    setText(root, '[data-owner-readiness-title]', title);
    setText(root, '[data-owner-readiness-copy]', text);
    setText(root, '[data-owner-readiness-badge]', badge);
  }

  function setSubmitLabel(submit, serving, strings) {
    if (!submit) return;
    submit.textContent = serving ? strings.submitPreview : strings.submitDraft;
  }

  /** @param {HTMLElement} root */
  function init(root) {
    if (!root || root.getAttribute('data-runtime-version') === VERSION) return;
    root.setAttribute('data-runtime-version', VERSION);

    var gate = window.GPROPERTY_DATA_GATE;
    var form = root.querySelector('[data-owner-form]');
    var submit = root.querySelector('[data-owner-submit]');
    var consent = root.querySelector('[data-owner-consent]');
    var panel = root.querySelector('[data-owner-result-panel]');
    if (!gate || !form || !submit || !consent) return;

    var strings = copy(root);
    var propertyKey = root.getAttribute('data-property-draft-key') || gate.canonicalDraftKey;
    var legacyPropertyKey =
      root.getAttribute('data-legacy-property-draft-key') || gate.legacyDraftKey;
    var flowKey = root.getAttribute('data-flow-draft-key');
    var stored = gate.readPropertyDraft(propertyKey, legacyPropertyKey);
    var property = stored && stored.draft;
    var requestedRecord = gate.opaque(
      new URLSearchParams(window.location.search || '').get('record_id')
    );
    var localRecord = gate.opaque(property && property.record_id);
    var routeMismatch = !!(requestedRecord && requestedRecord !== localRecord);
    var verified = !routeMismatch && gate.isVerifiedDraft(property);
    var serving = false;

    function focusPanel() {
      if (!panel) return;
      try {
        panel.focus({ preventScroll: true });
      } catch (_) {
        panel.focus();
      }
      panel.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    function hydrateAddress() {
      var addressStep = root.querySelector('[data-owner-step="address"]');
      if (routeMismatch) {
        setText(root, '[data-owner-address-name]', strings.noAddress);
        setText(root, '[data-owner-address-detail]', strings.routeMismatch);
        if (addressStep) addressStep.classList.add('is-blocked');
        return;
      }
      if (!verified) {
        setText(root, '[data-owner-address-name]', strings.noAddress);
        setText(root, '[data-owner-address-detail]', strings.noAddressDetail);
        if (addressStep) addressStep.classList.add('is-blocked');
        return;
      }
      var estate = property.estate_context;
      setText(root, '[data-owner-address-name]', gate.locationLabel(estate));
      setText(
        root,
        '[data-owner-address-detail]',
        [
          estate.address_zh || estate.address_en,
          estate.district_zh || estate.district_en,
          estate.coordinate_verified ? strings.coordinateVerified : strings.coordinateMissing,
        ]
          .filter(Boolean)
          .join(' · ')
      );
      if (addressStep) addressStep.classList.add('is-complete');
    }

    function hydrateOwnerConditions() {
      var draft = gate.read(flowKey);
      if (!draft || typeof draft !== 'object') return;
      ['owner_intent', 'property_type', 'expected_price', 'timeline', 'notes'].forEach(
        function (name) {
          if (form.elements.namedItem(name) && draft[name] != null)
            form.elements.namedItem(name).value = draft[name];
        }
      );
    }

    function collect() {
      return {
        schema_version: 'gproperty.v2',
        source: 'gproperty_owner_fit',
        type: 'preview',
        result_surface: 'workspace',
        record_id: gate.opaque(property && property.record_id),
        estate_context: verified ? property.estate_context : null,
        estate_name: verified ? gate.locationLabel(property.estate_context) : '',
        owner_intent: value(form, 'owner_intent'),
        property_type: value(form, 'property_type'),
        expected_price: value(form, 'expected_price'),
        timeline: value(form, 'timeline'),
        notes: value(form, 'notes'),
        consent: !!(
          form.elements.namedItem('consent') && form.elements.namedItem('consent').checked
        ),
        mock_data_used: false,
      };
    }

    function validate(payload) {
      if (routeMismatch) return strings.routeMismatch;
      if (!verified) return strings.noAddressDetail;
      if (!payload.owner_intent) return strings.intentRequired;
      if (serving && !payload.consent) return strings.consentRequired;
      return '';
    }

    function saveLocal(payload) {
      return gate.write(
        flowKey,
        Object.assign({}, payload, {
          consent: false,
          backend_submission: false,
          readiness_serving_ready: serving,
          updated_at: new Date().toISOString(),
        })
      );
    }

    function saveResultReference(resultId) {
      var safeResultId = gate.opaque(resultId);
      if (!safeResultId || !property || !gate.isVerifiedDraft(property)) return false;
      var references = Object.assign({}, property.fit_references || {});
      references.owner_fit = {
        result_id: safeResultId,
        status: 'ready',
        updated_at: new Date().toISOString(),
      };
      var next = Object.assign({}, property, {
        fit_references: references,
        updated_at: new Date().toISOString(),
      });
      var saved = gate.writePropertyDraft(propertyKey, next);
      if (saved) property = gate.readPropertyDraft(propertyKey, legacyPropertyKey).draft;
      return saved;
    }

    function success(body) {
      var data = body && body.data && typeof body.data === 'object' ? body.data : body;
      var resultId = gate.opaque((data && data.result_id) || (body && body.result_id));
      if (!resultId) throw new Error('owner_fit_contract_invalid');
      saveResultReference(resultId);
      setText(root, '[data-owner-result-title]', strings.resultTitle);
      setText(
        root,
        '[data-owner-result-copy]',
        safeConsumerText(gate, (data && data.summary) || (body && body.summary), strings.resultCopy)
      );
      setText(root, '[data-owner-result-status]', strings.resultStatus);
      setState(root, 'result');
      var resultStep = root.querySelector('[data-owner-step="result"]');
      if (resultStep) resultStep.classList.add('is-complete');
      focusPanel();
    }

    function post(payload) {
      var idempotencyKey =
        window.crypto && typeof window.crypto.randomUUID === 'function'
          ? window.crypto.randomUUID()
          : 'owner-fit-' + Date.now();
      setState(root, 'loading');
      setSubmitLabel(
        submit,
        true,
        Object.assign({}, strings, { submitPreview: strings.previewLoading })
      );
      focusPanel();

      gate
        .request(
          root.getAttribute('data-api-endpoint'),
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Idempotency-Key': idempotencyKey,
            },
            body: JSON.stringify(Object.assign({}, payload, { idempotency_key: idempotencyKey })),
          },
          20000
        )
        .then(function (response) {
          var body = response.body || {};
          if (gate.isFailClosed(response.http_status, body)) {
            serving = false;
            consent.hidden = true;
            setSubmitLabel(submit, false, strings);
            setState(root, 'blocked', strings.failClosed);
            focusPanel();
            return;
          }
          if (!response.response_ok || body.ok === false || body.success === false) {
            throw new Error(body.error || body.status || 'owner_fit_failed');
          }
          success(body);
        })
        .catch(function () {
          setState(root, 'error', strings.previewError);
          focusPanel();
        })
        .finally(function () {
          submit.disabled = false;
          setSubmitLabel(submit, serving, strings);
        });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var payload = collect();
      var problem = validate(payload);
      setText(root, '[data-owner-form-status]', problem);
      if (problem) {
        setState(root, verified && !routeMismatch ? 'idle' : 'blocked', problem);
        focusPanel();
        return;
      }
      if (!saveLocal(payload)) {
        setText(root, '[data-owner-form-status]', strings.localSaveFailed);
        return;
      }
      var conditionsStep = root.querySelector('[data-owner-step="conditions"]');
      if (conditionsStep) conditionsStep.classList.add('is-complete');
      if (!serving) {
        setState(root, 'blocked', strings.blockedCopy);
        setText(root, '[data-owner-form-status]', strings.localSaved);
        focusPanel();
        return;
      }
      post(payload);
    });

    hydrateAddress();
    hydrateOwnerConditions();
    readinessUI(
      root,
      'checking',
      strings.checkingTitle,
      strings.checkingCopy,
      strings.checkingBadge
    );

    gate
      .publicReadiness(root.getAttribute('data-readiness-endpoint'))
      .then(function (response) {
        var body = response.body || {};
        if (!response.response_ok) throw new Error(body.error || 'readiness_failed');
        if (gate.isServingReady(body)) {
          serving = true;
          readinessUI(root, 'ready', strings.readyTitle, strings.readyCopy, strings.readyBadge);
          consent.hidden = false;
          setSubmitLabel(submit, true, strings);
          setState(root, verified ? 'idle' : 'blocked', verified ? '' : strings.noAddressDetail);
          var readinessStep = root.querySelector('[data-owner-step="readiness"]');
          if (readinessStep) readinessStep.classList.add('is-complete');
        } else if (gate.isPublicRedacted(body)) {
          readinessUI(
            root,
            'blocked',
            strings.blockedTitle,
            strings.blockedCopy,
            strings.blockedBadge
          );
          consent.hidden = true;
          setSubmitLabel(submit, false, strings);
          setState(root, verified ? 'idle' : 'blocked', verified ? '' : strings.noAddressDetail);
        } else {
          readinessUI(
            root,
            'error',
            strings.readinessErrorTitle,
            strings.readinessErrorCopy,
            strings.blockedBadge
          );
          consent.hidden = true;
          setSubmitLabel(submit, false, strings);
          setState(root, 'blocked', strings.blockedCopy);
        }
      })
      .catch(function () {
        readinessUI(
          root,
          'error',
          strings.readinessErrorTitle,
          strings.readinessErrorCopy,
          strings.blockedBadge
        );
        consent.hidden = true;
        setSubmitLabel(submit, false, strings);
        setState(root, 'blocked', strings.blockedCopy);
      });
  }

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT)) init(target);
    all(target, ROOT).forEach(init);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }
})(window, document);
